import { Dialog, PlainButton } from "@jugl-web/ui-components";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { ListBoxItem } from "@jugl-web/ui-components/cross-platform/ListBox";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { Button } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import type {
  AgentTool,
  CalcomEventType,
  CalendlyEventType,
  SquareupLocation,
} from "@jugl-web/rest-api";
import { useAppVariant, useToast, useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import { ReactComponent as CalendlyIcon } from "../../assets/calendly.svg";
import { ReactComponent as SquareIcon } from "../../assets/square.svg";
import { ReactComponent as CalcomIcon } from "../../assets/calcom.svg";
import { INTEGRATION_PACK_TOOLS } from "../../consts";
import { ReactComponent as AutomationIcon } from "./assets/automation.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

type ConnectionProvider = "calendly" | "squareup" | "cal";

interface PickerItemValue {
  name: string;
  duration?: number;
  booking_url?: string;
  scheduleId?: number | null;
}

export interface ConnectionPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: ConnectionProvider;
  entityId: string;
  connectionId: string;
}

const PROVIDER_META: Record<
  ConnectionProvider,
  {
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
    brandLabelKey: string;
    brandLabelDefault: string;
    sectionLabelKey: string;
    sectionLabelDefault: string;
    sectionDescriptionKey: string;
    sectionDescriptionDefault: string;
  }
> = {
  calendly: {
    Icon: CalendlyIcon,
    brandLabelKey: "cx-actions-page.calendly",
    brandLabelDefault: "Calendly",
    sectionLabelKey: "cx-actions-page.calendly-event-type",
    sectionLabelDefault: "Select Event type",
    sectionDescriptionKey: "cx-actions-page.calendly-event-type-description",
    sectionDescriptionDefault:
      "When customer will schedule a meeting, indicated event type will be applied",
  },
  squareup: {
    Icon: SquareIcon,
    brandLabelKey: "cx-actions-page.square",
    brandLabelDefault: "Square",
    sectionLabelKey: "cx-actions-page.squareup-location",
    sectionLabelDefault: "Select Location",
    sectionDescriptionKey: "cx-actions-page.squareup-location-description",
    sectionDescriptionDefault:
      "When customer will make a purchase, indicated location will be applied",
  },
  cal: {
    Icon: CalcomIcon,
    brandLabelKey: "cx-actions-page.calcom",
    brandLabelDefault: "Cal.com",
    sectionLabelKey: "cx-actions-page.calcom-event-type",
    sectionLabelDefault: "Select Event type",
    sectionDescriptionKey: "cx-actions-page.calcom-event-type-description",
    sectionDescriptionDefault:
      "When customer will schedule a meeting, indicated event type will be applied",
  },
};

export const ConnectionPickerDialog: React.FC<ConnectionPickerDialogProps> = ({
  isOpen,
  onClose,
  provider,
  entityId,
  connectionId,
}) => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const { integrationsApi } = useRestApiProvider();

  const [setCalendlyEventType, { isLoading: isSavingCalendly }] =
    integrationsApi.useSetCalendlyEventTypeMutation();
  const [setCalcomEventType, { isLoading: isSavingCalcom }] =
    integrationsApi.useSetCalcomEventTypeMutation();
  const [setSquareupLocation, { isLoading: isSavingSquareup }] =
    integrationsApi.useSetSquareupLocationMutation();
  const [updateAgentConfig, { isLoading: isUpdatingAgentConfig }] =
    integrationsApi.useUpdateAgentConfigMutation();

  const { data: agentConfig } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });

  const eventTypesQuery = integrationsApi.useGetCalendlyEventTypesQuery(
    { entityId, connectionId },
    { skip: provider !== "calendly" }
  );
  const locationsQuery = integrationsApi.useGetSquareupLocationsQuery(
    { entityId, connectionId },
    { skip: provider !== "squareup" }
  );
  const calcomEventTypesQuery = integrationsApi.useGetCalcomEventTypesQuery(
    { entityId, connectionId },
    { skip: provider !== "cal" }
  );

  const items: ListBoxItem<PickerItemValue>[] = useMemo(() => {
    if (provider === "calendly") {
      return (eventTypesQuery.data?.data ?? []).map((item) => ({
        id: item.id,
        value: { name: item.name, duration: item.duration },
      }));
    }

    if (provider === "cal") {
      return (calcomEventTypesQuery.data?.data ?? []).map((item) => ({
        id: String(item.id),
        value: {
          name: item.name,
          duration: item.duration,
          booking_url: item.booking_url,
          scheduleId: item.scheduleId,
        },
      }));
    }

    return (locationsQuery.data?.data ?? []).map((item) => ({
      id: item.id,
      value: { name: item.name },
    }));
  }, [
    provider,
    eventTypesQuery.data,
    calcomEventTypesQuery.data,
    locationsQuery.data,
  ]);

  const isFetching =
    provider === "calendly"
      ? eventTypesQuery.isFetching
      : provider === "cal"
      ? calcomEventTypesQuery.isFetching
      : locationsQuery.isFetching;

  const [selectedItem, setSelectedItem] =
    useState<ListBoxItem<PickerItemValue> | null>(null);

  const isSaving =
    isSavingCalendly ||
    isSavingCalcom ||
    isSavingSquareup ||
    isUpdatingAgentConfig;

  const meta = PROVIDER_META[provider];
  const BrandIcon = meta.Icon;

  const enableProviderTools = async () => {
    if (!agentConfig) return;
    const newEntry: AgentTool = {
      pack: provider,
      connection_id: connectionId,
      tools: INTEGRATION_PACK_TOOLS[provider],
      params: {},
    };

    // The UI supports a single connection per provider, so there must be at
    // most one tool entry per pack. Drop any existing entries for this provider
    // (including stale ones left over from a previous connect/disconnect cycle)
    // before appending the fresh one.
    const existingTools = agentConfig.config.tools ?? [];
    const nextTools: AgentTool[] = [
      ...existingTools.filter((tool) => tool.pack !== provider),
      newEntry,
    ];

    await updateAgentConfig({
      entityId,
      config: { ...agentConfig.config, tools: nextTools },
    }).unwrap();
  };

  const handleSave = async () => {
    if (!selectedItem) return;

    try {
      if (provider === "calendly") {
        const payload: CalendlyEventType = {
          id: selectedItem.id,
          name: selectedItem.value.name,
          duration: selectedItem.value.duration ?? 0,
        };
        await setCalendlyEventType({
          entityId,
          connectionId,
          eventType: payload,
        }).unwrap();
      } else if (provider === "cal") {
        const payload: CalcomEventType = {
          id: Number(selectedItem.id),
          name: selectedItem.value.name,
          duration: selectedItem.value.duration ?? 0,
          booking_url: selectedItem.value.booking_url ?? "",
          scheduleId: selectedItem.value.scheduleId ?? null,
        };
        await setCalcomEventType({
          entityId,
          connectionId,
          eventType: payload,
        }).unwrap();
      } else {
        const payload: SquareupLocation = {
          id: selectedItem.id,
          name: selectedItem.value.name,
        };
        await setSquareupLocation({
          entityId,
          connectionId,
          location: payload,
        }).unwrap();
      }
    } catch {
      toast(
        t({
          id: "cx-actions-page.connection-save-failed",
          defaultMessage: "Failed to save selection. Please try again.",
        }),
        { variant: "error" }
      );
      return;
    }

    try {
      await enableProviderTools();
    } catch {
      toast(
        t({
          id: "cx-actions-page.tool-enable-failed",
          defaultMessage:
            "Connection saved. Couldn't enable tools automatically — try again from Setup & Test.",
        }),
        { variant: "error" }
      );
      onClose();
      return;
    }

    toast(
      t({
        id: "cx-actions-page.connection-success",
        defaultMessage: "Connected successfully",
      })
    );
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-full max-w-[640px] flex-col overflow-hidden rounded-2xl"
    >
      <div className="border-dark-100 flex h-[60px] shrink-0 items-center justify-between gap-6 border-0 border-b border-solid px-6">
        <div className="flex items-center gap-3">
          <AutomationIcon className="h-5 w-5" />
          <span className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "cx-actions-page.automation-details",
              defaultMessage: "Automation details",
            })}
          </span>
        </div>
        <PlainButton onClick={onClose} disabled={isSaving}>
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="flex flex-col gap-6 px-8 py-8">
        <div className="flex items-center gap-3">
          <BrandIcon className="h-8 w-8" />
          <span className="font-secondary text-2xl font-semibold text-[#1A2023]">
            {t({
              id: meta.brandLabelKey,
              defaultMessage: meta.brandLabelDefault,
            })}
          </span>
        </div>
        <div className="border-grey-300 flex flex-col gap-4 border-0 border-l-2 border-solid pl-4">
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-sm font-semibold text-[#1A2023]">
              {t({
                id: meta.sectionLabelKey,
                defaultMessage: meta.sectionLabelDefault,
              })}
            </span>
            <span className="text-dark-600 font-secondary text-sm">
              {t({
                id: meta.sectionDescriptionKey,
                defaultMessage: meta.sectionDescriptionDefault,
              })}
            </span>
          </div>
          <ResourcePickerPopover<PickerItemValue>
            placement="bottom-start"
            adjustToTriggerWidth
            isDisabled={isFetching || items.length === 0}
            selectionBehavior={{ mode: "single", canToggle: false }}
            defaultSelectedIds={selectedItem ? [selectedItem.id] : []}
            items={items}
            maxVisibleItems={6}
            renderLabel={(item) => item.value.name}
            renderSecondaryText={(item) =>
              typeof item.value.duration === "number" && item.value.duration > 0
                ? t(
                    {
                      id: "cx-actions-page.event-duration-minutes",
                      defaultMessage: "{duration} min",
                    },
                    { duration: item.value.duration }
                  )
                : null
            }
            onSelect={({ item, onClose: closePopover }) => {
              setSelectedItem(item);
              closePopover();
            }}
            renderTrigger={({ Trigger, triggerRef, isOpen: isPopoverOpen }) => (
              <Trigger
                ref={triggerRef}
                as={SelectTrigger}
                label={
                  selectedItem?.value.name ??
                  t({
                    id: "cx-actions-page.picker-select-placeholder",
                    defaultMessage: "Select",
                  })
                }
                isOpen={isPopoverOpen}
                hasValue={Boolean(selectedItem)}
                isDisabled={isFetching || items.length === 0}
              />
            )}
          />
        </div>
      </div>
      <div className="border-dark-100 flex shrink-0 justify-end border-0 border-t border-solid px-6 py-4">
        <Button
          onClick={handleSave}
          isDisabled={!selectedItem || isSaving}
          isLoading={isSaving}
          className="h-11 min-w-[160px]"
        >
          {t({ id: "common.save", defaultMessage: "Save" })}
        </Button>
      </div>
    </Dialog>
  );
};
